<script setup lang="ts" generic="T extends { key: string; value: string } | { key: number; value: string }">
const $attrs = useAttrs();
const { $gettext } = useGettext();

/********************
 * PROPS & EMITS    *
 ********************/
interface CiSelectProps {
  layout?: 'simple' | 'standard';
  options: Readonly<T[]>;
  value?: T;
  showDisabledOption?: boolean;
  disabledText?: string;
}
const props = withDefaults(defineProps<CiSelectProps>(), {
  layout: 'standard',
  showDisabledOption: false,
  disabledText: undefined,
});

const emit = defineEmits<{
  change: [value: string];
}>();

const styles = {
  simple: 'bg-transparent text-base font-medium',
  standard: 'bg-white rounded border border-primary',
};

const arrowColor = computed(() => {
  return props.value?.key === undefined ? 'rgb(var(--color-danger))' : 'rgb(var(--color-primary))';
});

const disabledTextDisplay = computed(() => {
  if (props.disabledText === undefined) {
    return $gettext('Please select');
  }
  return props.disabledText;
});

/********************
 * FUNCTIONS         *
 ********************/
function onChange($event: Event) {
  emit('change', ($event.target as HTMLSelectElement)?.value);
}
</script>

<template>
  <select
    :id="$attrs.name"
    class="ci-select relative h-10 cursor-pointer appearance-none bg-no-repeat py-0 pl-3.5 pr-8 text-sm outline-none"
    :class="[styles[props.layout], props.value?.key === undefined ? '!border-danger text-danger' : 'text-primary']"
    :style="
      `background-image: linear-gradient(45deg, transparent 50%, ${arrowColor} 50%),
        linear-gradient(135deg, ${arrowColor} 50%, transparent 50%);
      background-position:
        calc(100% - 20px) 50%,
        calc(100% - 15px) 50%;
      background-size:
        5px 5px,
        5px 5px,
        1px 1.5em;`
    "
    :value="props.value?.key"
    @change="onChange"
  >
    <option
      v-if="props.showDisabledOption"
      :selected="!props.value?.key"
      value=""
      disabled
    >
      {{ disabledTextDisplay }}
    </option>
    <option
      v-for="option in props.options"
      :key="option.key"
      :value="option.key"
      :selected="option.key === props.value?.key"
    >
      {{ option.value }}
    </option>
  </select>
</template>
